import React from "react";
import { MapPin, Phone, Clock, MessageCircle } from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";
import { whatsappUrl, reservationMessage, WHATSAPP_DISPLAY } from "../../lib/whatsapp";

export default function Footer() {
  const { t } = useLanguage();
  const year = new Date().getFullYear();
  const waReservation = whatsappUrl(reservationMessage(t));

  return (
    <footer
      id="contact"
      data-testid="site-footer"
      className="relative bg-[#1A1A1A] text-[#F9F6F0]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left: info */}
          <div className="lg:col-span-5">
            <h2
              data-testid="footer-title"
              className="font-serif-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-white"
            >
              {t.footer.title}
            </h2>

            <div className="mt-10 space-y-6">
              <InfoRow
                icon={<MapPin className="h-5 w-5 text-[#E8A87C]" />}
                label={t.nav.contact}
                value={t.footer.address}
                testid="footer-address"
              />
              <InfoRow
                icon={<MessageCircle className="h-5 w-5 text-[#E8A87C]" />}
                label={t.whatsapp.buttonShort}
                value={WHATSAPP_DISPLAY}
                href={waReservation}
                target="_blank"
                testid="footer-whatsapp"
              />
              <InfoRow
                icon={<Phone className="h-5 w-5 text-[#E8A87C]" />}
                label={t.footer.phoneLabel}
                value="671 71 23 35"
                href="tel:+34671712335"
                testid="footer-phone"
              />
              <InfoRow
                icon={<Clock className="h-5 w-5 text-[#E8A87C]" />}
                label={t.footer.scheduleLabel}
                value={t.footer.schedule}
                testid="footer-schedule"
              />
            </div>

            <div className="mt-12 flex flex-col gap-3">
              <a
                href={waReservation}
                target="_blank"
                rel="noreferrer"
                data-testid="footer-whatsapp-cta"
                className="inline-flex items-center justify-center gap-3 bg-[#25D366] hover:bg-[#1ebe5b] text-white px-10 py-5 text-sm tracking-[0.2em] uppercase font-semibold transition-colors"
              >
                <MessageCircle className="h-5 w-5" />
                {t.whatsapp.buttonLabel} · {WHATSAPP_DISPLAY}
              </a>
              <a
                href="tel:+34671712335"
                data-testid="footer-call-cta"
                className="inline-flex items-center justify-center gap-3 bg-transparent border border-white/40 hover:bg-white/10 text-white px-10 py-5 text-sm tracking-[0.2em] uppercase font-semibold transition-colors"
              >
                <Phone className="h-5 w-5" />
                {t.footer.cta}
              </a>
            </div>
          </div>

          {/* Right: map */}
          <div className="lg:col-span-7">
            <div className="relative w-full h-[380px] md:h-[520px] border border-[#3a3a3a]">
              <iframe
                data-testid="footer-map"
                title="GR a Cuchillo location"
                src="https://www.google.com/maps?q=C.+Cruz+de+la+Mujer,+2b,+41210+Guillena,+Sevilla&output=embed"
                className="w-full h-full grayscale-[40%]"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-[#3a3a3a] flex flex-col md:flex-row gap-4 justify-between items-start md:items-center text-xs text-[#9b958a]">
          <div data-testid="footer-built">{t.footer.built}</div>
          <div data-testid="footer-rights">© {year} · {t.footer.rights}</div>
        </div>
      </div>
    </footer>
  );
}

function InfoRow({ icon, label, value, href, target, testid }) {
  const content = (
    <div className="flex items-start gap-4" data-testid={testid}>
      <div className="mt-1">{icon}</div>
      <div>
        <div className="text-[0.65rem] uppercase tracking-[0.2em] text-[#9b958a] mb-1">
          {label}
        </div>
        <div className="text-base md:text-lg text-white">{value}</div>
      </div>
    </div>
  );
  return href ? (
    <a
      href={href}
      target={target}
      rel={target === "_blank" ? "noreferrer" : undefined}
      className="block hover:opacity-80 transition-opacity"
    >
      {content}
    </a>
  ) : (
    content
  );
}
